import type { TreeOption } from 'naive-ui'

/**
 * 文件定位：
 * 1. 当前文件位于 `web-client/core/feature-tree` 目录；
 * 2. 负责构建 SolidWorks FeatureManager 风格的系统节点；
 * 3. 原生 CAD 投影与 GLB/装配投影共同复用，避免默认节点顺序在多条链路中漂移。
 */

/**
 * 实体文件夹节点构建输入。
 */
export interface FeatureManagerBodyFolderNodeInput {
  /** 节点 key。 */
  key: string
  /** 实体子节点列表。 */
  bodyNodes: TreeOption[]
  /** 是否默认折叠。 */
  defaultCollapsed?: boolean
}

/**
 * 材质节点构建输入。
 */
export interface FeatureManagerMaterialNodeInput {
  /** 节点 key。 */
  key: string
  /** 材质显示名称。 */
  materialLabel?: string
  /** 节点类型，原生投影保留 `material`，GLB 投影保留普通分组语义。 */
  type?: string
}

/**
 * 创建历史记录节点。
 *
 * @param key 节点 key。
 * @returns 历史记录树节点。
 */
export function createFeatureManagerHistoryNode(key: string): TreeOption {
  return {
    key,
    label: '历史记录',
    type: 'group',
    iconType: 'history',
    isLeaf: true,
  }
}

/**
 * 创建注解节点。
 *
 * @param key 节点 key。
 * @returns 注解树节点。
 */
export function createFeatureManagerAnnotationsNode(key: string): TreeOption {
  return {
    key,
    label: '注解',
    type: 'group',
    iconType: 'annotation',
    children: [],
    isLeaf: true,
  }
}

/**
 * 创建实体文件夹节点。
 *
 * @param input 实体文件夹输入。
 * @returns 实体文件夹树节点。
 */
export function createFeatureManagerBodyFolderNode(
  input: FeatureManagerBodyFolderNodeInput,
): TreeOption {
  // 实体数量必须从子节点实时推导，避免导入模型合并实体后标签计数失真。
  const bodyCount = input.bodyNodes.length

  return {
    key: input.key,
    label: `实体(${bodyCount})`,
    type: 'group',
    iconType: 'body-folder',
    children: input.bodyNodes,
    isLeaf: bodyCount === 0,
    defaultCollapsed: input.defaultCollapsed,
  }
}

/**
 * 创建材质节点。
 *
 * @param input 材质节点输入。
 * @returns 材质树节点。
 */
export function createFeatureManagerMaterialNode(
  input: FeatureManagerMaterialNodeInput,
): TreeOption {
  // SolidWorks 未指定材质时仍保留材质节点，并显示 `<未指定>`。
  const materialLabel = input.materialLabel?.trim() || '未指定'

  return {
    key: input.key,
    label: `材质 <${materialLabel}>`,
    type: input.type ?? 'material',
    iconType: 'material',
    isLeaf: true,
  }
}
